/**
 * SaveCardOnFileDialog (now SavePaymentMethodDialog under the hood)
 *
 * Admin / staff tool to securely save a customer's payment method to
 * Stripe — NOT to our database. Supports two payment method types:
 *   • Card (MOTO / cardholder-not-present)
 *   • US bank account (ACH) via Stripe Financial Connections
 *
 * Flow:
 *   1. Open dialog → admin picks Card or ACH tab.
 *   2. POST /api/stripe/admin/setup-intent with the chosen pm_type →
 *      backend creates a Stripe Customer (or reuses one), creates a
 *      SetupIntent (`usage="off_session"`) with the requested
 *      payment_method_types, returns client_secret + publishable_key.
 *   3. Card path: Stripe `CardElement` + `confirmCardSetup`.
 *      ACH path: Stripe `confirmUsBankAccountSetup` opens Financial
 *      Connections (instant) or falls back to manual routing/account
 *      with micro-deposit verification.
 *   4. On success → POST /api/stripe/admin/confirm-saved-card so the
 *      backend audit-logs the save and emails the customer.
 *
 * Props:
 *   - open, onOpenChange
 *   - parent: { id, name, email }   (works for both parent AND customer roles)
 *   - onSaved: (result) => void
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import {
  AlertCircle,
  Building2,
  CreditCard,
  Loader2,
  ShieldCheck,
} from 'lucide-react';
import api from '../../services/api';

// Cache the Stripe.js loader per publishable-key — re-instantiating it
// thrashes the iframe.
const stripeLoaderCache = {};
function getStripePromise(publishableKey) {
  if (!publishableKey) return null;
  if (!stripeLoaderCache[publishableKey]) {
    stripeLoaderCache[publishableKey] = loadStripe(publishableKey);
  }
  return stripeLoaderCache[publishableKey];
}

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '15px',
      color: '#1e293b',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      '::placeholder': { color: '#94a3b8' },
    },
    invalid: { color: '#dc2626', iconColor: '#dc2626' },
  },
  hidePostalCode: false,
};

// ============================================================
// Card form
// ============================================================
function CardInputForm({ parent, clientSecret, setupIntentId, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState(null);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!stripe || !elements) return;
    if (!acknowledged) {
      toast.error('Please confirm cardholder consent before saving.');
      return;
    }
    setSubmitting(true);
    try {
      const cardElement = elements.getElement(CardElement);
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: { name: parent.name || '', email: parent.email || '' },
        },
      });
      if (result.error) {
        const msg = result.error.message || 'Failed to save card';
        setCardError(msg);
        toast.error(msg, { duration: 7000 });
        return;
      }
      const pmId = result.setupIntent?.payment_method;
      if (!pmId) {
        toast.error('Card saved with Stripe but missing PaymentMethod ID — please reload.');
        return;
      }
      try {
        const { data } = await api.post('/stripe/admin/confirm-saved-card', {
          parent_id: parent.id,
          setup_intent_id: setupIntentId,
          payment_method_id: pmId,
        });
        toast.success(`Saved: ${data.display_label || data.card?.brand || 'Card'}`, {
          duration: 6000,
          description: data.notification_email_sent
            ? `Notification email sent to ${parent.email}`
            : 'Notification email could not be sent — please notify the customer manually.',
        });
        onSuccess?.(data);
      } catch (err) {
        const detail = err.response?.data?.detail;
        toast.error(
          `Card saved at Stripe but audit confirmation failed: ${typeof detail === 'string' ? detail : 'unknown'}`,
          { duration: 9000 },
        );
        onSuccess?.({ partial: true, payment_method_id: pmId });
      }
    } catch (err) {
      console.error('[SaveCardOnFile] confirmCardSetup threw', err);
      toast.error(err.message || 'Unexpected error saving card');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="save-card-form">
      <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 flex gap-2">
        <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <div className="font-semibold mb-1">MOTO / Cardholder-not-present</div>
          You are saving this card on behalf of{' '}
          <strong>{parent.name || parent.email}</strong>. By proceeding, you confirm the
          cardholder has authorized this academy to store their card for future charges.
        </div>
      </div>

      <div>
        <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Card details</Label>
        <div className="rounded-lg border border-slate-300 bg-white px-3 py-2.5 focus-within:border-slate-900 focus-within:ring-1 focus-within:ring-slate-900">
          <CardElement
            options={CARD_ELEMENT_OPTIONS}
            onChange={(ev) => {
              setCardComplete(ev.complete);
              setCardError(ev.error?.message || null);
            }}
          />
        </div>
        {cardError && (
          <p className="text-xs text-red-600 mt-1.5" data-testid="card-error">
            {cardError}
          </p>
        )}
      </div>

      <div className="flex items-start gap-2 pt-1">
        <Checkbox
          id="moto-ack"
          checked={acknowledged}
          onCheckedChange={(v) => setAcknowledged(!!v)}
          data-testid="moto-ack-checkbox"
        />
        <Label htmlFor="moto-ack" className="text-xs text-slate-700 leading-snug cursor-pointer">
          I confirm the cardholder authorized this card to be saved for future charges. This will
          be audit-logged and the customer will receive an email notification.
        </Label>
      </div>

      <DialogFooter className="gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!stripe || !cardComplete || !acknowledged || submitting}
          data-testid="save-card-submit-btn"
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Saving…
            </>
          ) : (
            <>
              <ShieldCheck className="h-4 w-4 mr-1.5" /> Save Card to File
            </>
          )}
        </Button>
      </DialogFooter>
    </form>
  );
}

// ============================================================
// ACH (US bank account) form — uses Stripe Financial Connections
// ============================================================
function AchBankForm({ parent, clientSecret, setupIntentId, onSuccess, onCancel }) {
  const stripe = useStripe();
  const [submitting, setSubmitting] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [bankError, setBankError] = useState(null);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!stripe) return;
    if (!acknowledged) {
      toast.error('Please confirm authorization before linking the bank account.');
      return;
    }
    setSubmitting(true);
    try {
      // Stripe opens its hosted Financial Connections flow → customer
      // links bank instantly. Falls back to manual routing/account
      // entry + micro-deposit if FC isn't available for that bank.
      const result = await stripe.collectBankAccountForSetup({
        clientSecret,
        params: {
          payment_method_type: 'us_bank_account',
          payment_method_data: {
            billing_details: {
              name: parent.name || parent.email || 'Customer',
              email: parent.email || '',
            },
          },
        },
      });
      if (result.error) {
        const msg = result.error.message || 'Failed to link bank account';
        setBankError(msg);
        toast.error(msg, { duration: 7000 });
        return;
      }

      // FC flow returned a PaymentMethod attached to the SetupIntent.
      // Stripe still needs the SetupIntent confirmed to mark the PM as
      // saved off-session. For instantly verified accounts this
      // confirms synchronously. For micro-deposit accounts it leaves
      // the SI in `requires_action` — the customer will need to
      // verify the deposit amounts later from their email.
      const confirmResult = await stripe.confirmUsBankAccountSetup(clientSecret);
      if (confirmResult.error) {
        const msg = confirmResult.error.message || 'Failed to confirm bank account';
        setBankError(msg);
        toast.error(msg, { duration: 7000 });
        return;
      }
      const si = confirmResult.setupIntent;
      const pmId = si?.payment_method;
      if (!pmId) {
        toast.error('Bank linked at Stripe but missing PaymentMethod ID — please reload.');
        return;
      }

      try {
        const { data } = await api.post('/stripe/admin/confirm-saved-card', {
          parent_id: parent.id,
          setup_intent_id: setupIntentId,
          payment_method_id: pmId,
        });

        if (si.status === 'requires_action') {
          toast.info(
            `Bank linked — verification pending. ${parent.email} will receive an email with micro-deposit amounts to confirm.`,
            { duration: 9000 },
          );
        } else {
          toast.success(`Saved: ${data.display_label || 'Bank account'}`, {
            duration: 6000,
            description: data.notification_email_sent
              ? `Notification email sent to ${parent.email}`
              : 'Notification email could not be sent — please notify the customer manually.',
          });
        }
        onSuccess?.(data);
      } catch (err) {
        const detail = err.response?.data?.detail;
        toast.error(
          `Bank saved at Stripe but audit confirmation failed: ${typeof detail === 'string' ? detail : 'unknown'}`,
          { duration: 9000 },
        );
        onSuccess?.({ partial: true, payment_method_id: pmId });
      }
    } catch (err) {
      console.error('[SaveCardOnFile] collectBankAccountForSetup threw', err);
      toast.error(err.message || 'Unexpected error linking bank account');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="save-ach-form">
      <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900 flex gap-2">
        <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <div className="font-semibold mb-1">Authorized ACH / Bank transfer</div>
          You are linking a US bank account on behalf of{' '}
          <strong>{parent.name || parent.email}</strong> for future ACH charges. Stripe will
          open a secure window so the customer can link instantly OR enter routing/account
          details for micro-deposit verification.
        </div>
      </div>

      {bankError && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">
          {bankError}
        </p>
      )}

      <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-3">
        Click <strong>Link Bank Account</strong> below. A Stripe-hosted secure window will
        open. The customer's bank credentials are <strong>never</strong> sent to our servers
        — Stripe handles them directly. ACH transfers settle in 3–5 business days and skip
        the 3% card surcharge.
      </div>

      <div className="flex items-start gap-2 pt-1">
        <Checkbox
          id="ach-ack"
          checked={acknowledged}
          onCheckedChange={(v) => setAcknowledged(!!v)}
          data-testid="ach-ack-checkbox"
        />
        <Label htmlFor="ach-ack" className="text-xs text-slate-700 leading-snug cursor-pointer">
          I confirm the account holder authorized this bank account to be saved for future
          ACH charges. This will be audit-logged and they will receive an email notification.
        </Label>
      </div>

      <DialogFooter className="gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!stripe || !acknowledged || submitting}
          data-testid="save-ach-submit-btn"
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Linking…
            </>
          ) : (
            <>
              <Building2 className="h-4 w-4 mr-1.5" /> Link Bank Account
            </>
          )}
        </Button>
      </DialogFooter>
    </form>
  );
}

// ============================================================
// Outer dialog
// ============================================================
export default function SaveCardOnFileDialog({ open, onOpenChange, parent, onSaved }) {
  const [setupIntent, setSetupIntent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pmType, setPmType] = useState('card'); // 'card' | 'us_bank_account'

  // Re-create the SetupIntent whenever the user switches tabs (each
  // tab needs a SetupIntent with the matching payment_method_types).
  useEffect(() => {
    if (!open || !parent?.id) {
      setSetupIntent(null);
      setError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setSetupIntent(null);
      try {
        const { data } = await api.post('/stripe/admin/setup-intent', {
          parent_id: parent.id,
          payment_method_type: pmType,
        });
        if (!cancelled) setSetupIntent(data);
      } catch (err) {
        const detail = err.response?.data?.detail;
        const msg = typeof detail === 'string' ? detail : 'Failed to start save flow';
        if (!cancelled) {
          setError(msg);
          toast.error(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, parent?.id, pmType]);

  // Reset tab to "card" whenever the dialog is closed so it doesn't
  // surprise the admin next time they open it.
  useEffect(() => {
    if (!open) setPmType('card');
  }, [open]);

  const stripePromise = useMemo(
    () => getStripePromise(setupIntent?.publishable_key),
    [setupIntent?.publishable_key],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="save-card-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
            Save Payment Method on File
          </DialogTitle>
          <DialogDescription>
            {parent?.name && (
              <>
                For <strong>{parent.name}</strong> ({parent.email})
              </>
            )}
            {!parent?.name && parent?.email}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={pmType} onValueChange={setPmType}>
          <TabsList className="grid grid-cols-2 w-full" data-testid="save-pm-tabs">
            <TabsTrigger value="card" data-testid="tab-save-card">
              <CreditCard className="h-4 w-4 mr-1.5" />
              Card
            </TabsTrigger>
            <TabsTrigger value="us_bank_account" data-testid="tab-save-ach">
              <Building2 className="h-4 w-4 mr-1.5" />
              Bank (ACH)
            </TabsTrigger>
          </TabsList>

          {loading && (
            <div className="py-10 flex flex-col items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin" />
              Preparing secure form…
            </div>
          )}

          {error && !loading && (
            <div className="py-6 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
              {error}
            </div>
          )}

          {setupIntent && stripePromise && !loading && !error && (
            <Elements
              stripe={stripePromise}
              options={{ clientSecret: setupIntent.client_secret }}
            >
              <TabsContent value="card" className="mt-4">
                <CardInputForm
                  parent={setupIntent.parent}
                  clientSecret={setupIntent.client_secret}
                  setupIntentId={setupIntent.setup_intent_id}
                  onSuccess={(result) => {
                    onSaved?.(result);
                    onOpenChange?.(false);
                  }}
                  onCancel={() => onOpenChange?.(false)}
                />
              </TabsContent>
              <TabsContent value="us_bank_account" className="mt-4">
                <AchBankForm
                  parent={setupIntent.parent}
                  clientSecret={setupIntent.client_secret}
                  setupIntentId={setupIntent.setup_intent_id}
                  onSuccess={(result) => {
                    onSaved?.(result);
                    onOpenChange?.(false);
                  }}
                  onCancel={() => onOpenChange?.(false)}
                />
              </TabsContent>
            </Elements>
          )}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
